import { For, Show, createMemo, createSignal, createEffect, onCleanup, onMount } from "solid-js";
import type { Accessor, Component } from "solid-js";
import { useShellStore } from "@ui/state/shell";
import { sendChatMessage, subscribeToChatMessages, chatMessages as chatMessagesSignal } from "../service.v2";

type MaybeAccessor<T> = T | Accessor<T> | null | undefined;

const read = <T,>(input: MaybeAccessor<T>): T | undefined =>
  typeof input === "function" ? (input as Accessor<T>)() : input ?? undefined;

const formatTimestamp = (date: Date | null): string => {
  if (!date) {
    return "Unknown";
  }
  const now = new Date();
  const diffMinutes = Math.round((now.getTime() - date.getTime()) / 1000 / 60);

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  return date.toLocaleDateString();
};

const formatDayLabel = (date: Date): string => {
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) return "Today";
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const ChatThreadPanel: Component = () => {
  const { state } = useShellStore();

  const selectedChatId = () => state.selectedChatId;

  const [newMessage, setNewMessage] = createSignal("");
  const [sending, setSending] = createSignal(false);
  const [sendError, setSendError] = createSignal<string | null>(null);

  let messagesContainerRef: HTMLDivElement | undefined;

  // Get messages from real-time signal
  const messages = createMemo(() => {
    const chatId = selectedChatId();
    if (!chatId) return [];
    return chatMessagesSignal().get(chatId) || [];
  });

  // Group messages by day
  const groupedMessages = createMemo(() => {
    const msgs = messages();
    const groups: Array<{ label: string; items: typeof msgs }> = [];
    let currentLabel: string | null = null;
    let currentItems: typeof msgs = [];

    msgs.forEach((message) => {
      const label = formatDayLabel(message.createdAt);
      if (label !== currentLabel) {
        if (currentItems.length > 0 && currentLabel) {
          groups.push({ label: currentLabel, items: currentItems });
        }
        currentLabel = label;
        currentItems = [];
      }
      currentItems.push(message);
    });

    if (currentItems.length > 0 && currentLabel) {
      groups.push({ label: currentLabel, items: currentItems });
    }
    return groups;
  });

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    if (messagesContainerRef) {
      messagesContainerRef.scrollTop = messagesContainerRef.scrollHeight;
    }
  };

  // Scroll to bottom when messages update
  createEffect(() => {
    messages(); // Track messages changes
    setTimeout(scrollToBottom, 100); // Delay to ensure DOM is updated
  });

  // Subscribe to real-time messages when chatId changes
  createEffect(() => {
    const chatId = selectedChatId();
    if (!chatId) return;

    console.log(`🔄 Subscribing to messages for chat ${chatId}`);
    const unsubscribe = subscribeToChatMessages(chatId);

    // Scroll to bottom when chat changes
    setTimeout(scrollToBottom, 200);

    onCleanup(() => {
      console.log(`🧹 Cleaning up subscription for chat ${chatId}`);
      unsubscribe();
    });
  });

  const handleSendMessage = async (event: Event) => {
    event.preventDefault();
    const chatId = selectedChatId();
    const message = newMessage().trim();

    if (!chatId || !message || sending()) {
      return;
    }

    setSendError(null);
    setSending(true);

    try {
      // Send message using V2 service - direct Firestore write
      await sendChatMessage({ chatId, text: message });
      setNewMessage("");

      // No need to invalidate queries - real-time subscription will update automatically
      console.log(`✅ Message sent to chat ${chatId}`);
    } catch (error) {
      console.error("Failed to send chat message", error);
      const messageText = error instanceof Error ? error.message : "Unable to send message. Please try again.";
      setSendError(messageText);
    } finally {
      setSending(false);
    }
  };

  return (
    <div class="chat-sidebar-panel">
      <Show when={selectedChatId()} fallback={<div class="chat-empty">Select a conversation to view messages.</div>}>
        <div class="chat-messages" ref={messagesContainerRef}>
          <Show when={messages().length > 0} fallback={<div class="chat-empty">No messages yet.</div>}>
            <For each={groupedMessages()}>
              {(group) => (
                <div class="chat-day-group">
                  <div class="chat-day-label">{group.label}</div>
                  <For each={group.items}>
                    {(message) => {
                      const isUser = message.senderType === "user";
                      const isAdmin = message.senderType === "admin";
                      const isSystem = message.senderType === "system";
                      const isAI = message.senderType === "ai";

                      return (
                        <div
                          class={`chat-message ${isUser ? "user" : "bot"} ${isAdmin ? "from-admin" : ""} ${isSystem ? "from-system" : ""} ${isAI ? "from-ai" : ""}`}
                        >
                          <div class="message-bubble">
                            <p innerHTML={message.text.replace(/\n/g, '<br>')}></p>

                            {/* Render action buttons if present */}
                            <Show when={message.metadata?.actions && message.metadata.actions.length > 0}>
                              <div class="message-actions">
                                <For each={message.metadata.actions}>
                                  {(action) => (
                                    <Show when={action.type === "link" && action.url && action.label}>
                                      <a
                                        href={action.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="message-action-button"
                                      >
                                        {action.label}
                                      </a>
                                    </Show>
                                  )}
                                </For>
                              </div>
                            </Show>
                          </div>
                          <div class="message-time">{formatTimestamp(message.createdAt)}</div>
                        </div>
                      );
                    }}
                  </For>
                </div>
              )}
            </For>
          </Show>
        </div>

        <div class="chat-input-section">
          <form class="chat-input-container" onSubmit={handleSendMessage}>
            <input
              type="text"
              class="chat-input"
              placeholder="Type your message..."
              value={newMessage()}
              onInput={(event) => {
                setNewMessage(event.currentTarget.value);
                if (sendError()) {
                  setSendError(null);
                }
              }}
              disabled={sending()}
            />
            <button
              type="submit"
              class="chat-send"
              disabled={sending() || !newMessage().trim()}
              aria-label="Send message"
            >
              <i class="fa-solid fa-paper-plane"></i>
            </button>
          </form>
          <Show when={sendError()}>
            {(errorText) => <p class="error-detail">{errorText()}</p>}
          </Show>
        </div>
      </Show>
    </div>
  );
};

